import { FieldPolicy } from "@apollo/client";
import { addDays, format, parseISO, subWeeks } from "date-fns";
import { getDayInPast } from "src/components/FormattedDate";
import { Scalars } from "src/generated/graphql-types";

export function formatWithYear(date: Scalars["Date"]) {
  return format(date, "MM/dd/yyyy");
}

export function formatWithShortYear(date: Scalars["Date"]) {
  return format(date, "MM/dd/yy");
}

export function formatShort(date: Scalars["Date"]) {
  return format(date, "M/d/yy");
}

export function formatYearFirst(date: Scalars["Date"]) {
  return format(date, "yyyy-MM-dd");
}

// Same formatting as the medium form type of Beam's DateField
// Ex: Mon, Jan 1
export function formatMedium(date: Scalars["Date"]) {
  return format(date, "EEE, MMM d");
}

// We unfortunately use different date utils in different places.
// DatePicker uses Moment, most everything else uses date-fns
// Holding both variations until we can consolidate
//
// I.e. Tuesday June 2, 2020
export const withDayFormatMoment = "dddd MMMM D, YYYY";
export const withDayFormatDateFns = "EEEE MMMM d, yyyy";
export function formatWithDay(date: Scalars["Date"]) {
  return format(date, withDayFormatDateFns);
}

// Ex: Mar 1, 2020
export const monthDayYearFormatMoment = "MMM D, YYYY";
export const monthDayYearFormatFns = "MMM d, yyyy";
export function formatMonthDayYear(date: Scalars["Date"]) {
  return format(date, monthDayYearFormatFns);
}

export function formatMonthDay(date: Scalars["Date"]) {
  return format(date, "MMM d");
}

// Used to break structural typing between Date and DateOnly, because we don't
// want non-DateOnly Date's being passed to GraphQL queries/mutations as-is.
//
// (Because when they're put on the wire, the Date.toJSON method will include
// timestamp information and the GraphQL server will reject it as an invalid type).
const NotDate = Symbol();

/**
 * A calendar day, i.e. a date without any time or time zone.
 *
 * Constructed by calling `new DateOnly(anExistingDate)`.
 */
export class DateOnly extends Date {
  constructor(public date: Date) {
    super(date.getTime());
  }

  toString() {
    return this.toISOString().split("T")[0];
  }

  toJSON() {
    return this.toString();
  }

  [NotDate]: undefined;
}

export function addDaysDateOnly(date: DateOnly, days: number) {
  return new DateOnly(addDays(date.date, days));
}

/** An alias for Date that specifically has time (unlike DateOnly). */
export type DateTime = Date;

export const dateTypePolicy: FieldPolicy<DateOnly, string | DateOnly, string> = {
  merge: (_, incoming) => {
    if (isNullOrUndefined(incoming)) {
      // It's important for these methods to return null if passed null
      return incoming;
    } else if (incoming instanceof DateOnly) {
      // In tests our mocks already have DateOnly
      return incoming;
    } else if ((incoming as any) instanceof Date) {
      // Somehow a story is getting a `Date` even though it's calling `newDate` through a factory...
      return new DateOnly(incoming as any as Date);
    } else {
      return new DateOnly(parseISO(incoming as string));
    }
  },
};

function formatDateOnly(incoming: string | Readonly<DateOnly>) {
  if (isNullOrUndefined(incoming)) {
    // It's important for these methods to return null if passed null
    return incoming;
  } else if (incoming instanceof DateOnly) {
    // In tests our mocks already have DateOnly
    return incoming;
  } else if ((incoming as any) instanceof Date) {
    // Somehow a story is getting a `Date` even though it's calling `newDate` through a factory...
    return new DateOnly(incoming as any as Date);
  } else {
    return new DateOnly(parseISO(incoming as string));
  }
}

/**
 * Ensures the date is really a DateOnly.
 *
 * Unfortunately mutation results will still have string dates, see:
 * https://github.com/apollographql/apollo-client/issues/7665
 *
 * Note that this is only required when you are directly accessing the
 * result of a mutation call. Any data that goes through the cache and
 * then updates other queries on the page will be fine.
 */
export function ensureDateOnly(date: DateOnly | string): DateOnly {
  if (typeof date === "string") {
    return new DateOnly(parseISO(date));
  } else {
    return date;
  }
}

export const dateTimeTypePolicy: FieldPolicy<DateTime, string | Date, string> = {
  merge: (_, incoming) => {
    if (isNullOrUndefined(incoming)) {
      return incoming;
    } else if (incoming instanceof Date) {
      return incoming;
    } else {
      return parseISO(incoming as string);
    }
  },
};

export const dateArrayTypePolicy: FieldPolicy<DateOnly[], string[] | DateOnly[], string> = {
  merge: (_, incoming) => {
    if (isNullOrUndefined(incoming)) return incoming;

    return incoming.map((date) => formatDateOnly(date));
  },
};

function isNullOrUndefined(param: any | null | undefined): param is null | undefined {
  return param === null || param === undefined;
}

export function formatWithDateAndTime(date: Scalars["Date"] | Date) {
  return format("date" in date ? date.date : date, "MM/dd/yy 'at' h:mmaaa");
}

export const lastSunday = getDayInPast("Sun");
export const twoSundaysAgo = subWeeks(lastSunday, 1);
